import React, { useEffect, useState, useRef } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Accordion,
} from "react-bootstrap";
import { AgGridReact } from 'ag-grid-react';

import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';

import { getFromLS, setToLS } from "../../utils/storage";
import styles from "./internalK1.module.css";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Switch from '../../components/Switch';
import { FaTrashAlt, FaQuestionCircle } from 'react-icons/fa';
import InfoModal from "./InfoModal/InfoModal";
import { Info } from "react-bootstrap-icons";

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_INTERNAL_K1_BASE_URL;

interface PartnershipInfo {
  name: string;
  ein: string;
  returnId: string;
}

interface Partner {
  id: string;
  name: string;
  ssn: string;
  entity_type: string;
  return_id_options: string[];
  selected: boolean;
  selectedReturn: string;
  selectedOutputVersion?: string;
  status: string;
  statusMessage?: string;
}

interface PartnerStatus {
  status: string;
  message?: string;
}

interface BatchStatusResponse {
  status: string;
  partners?: Record<string, PartnerStatus>;
  parents?: Record<string, PartnerStatus>;
}

interface FlowHistoryItem {
    timestamp: string;
    partnership_name: string;
    partnership_return_id: string;
    partner_name: string;
    partner_return_id: string;
    dest_version: string;
    status: string;
    message: string;
}

interface FlowHistoryResponse {
    status: string;
    data: FlowHistoryItem[];
}

interface ChildModeState {
    ein: string;
    availableReturns: string[];
    selectedReturn: string;
    partnershipInfo: PartnershipInfo;
    partners: Partner[];
}

interface ParentModeState {
    ein: string;
    partnershipInfo: PartnershipInfo;
    partners: Partner[];
    childReturnVersion: string;
    childOutputVersion: string;
    childReturnOptions: string[];
}

/**
 * internalK1 Parent Component
 * This component serves as the main container and can have child components inside "components/" folder.
 */
const InternalK1: React.FC = () => {
    // Common state
    const [status, setStatus] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [batchId, setBatchId] = useState<string | null>(null);
    const [isLoadingReturns, setIsLoadingReturns] = useState<boolean>(false);
    const [showHistory, setShowHistory] = useState<boolean>(false);
    const [history, setHistory] = useState<FlowHistoryItem[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
    const [isFetchParent, setIsFetchParent] = useState<boolean>(false);

    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

    // Child mode state
    const [childModeState, setChildModeState] = useState<ChildModeState>({
        ein: '',
        availableReturns: [],
        selectedReturn: '',
        partnershipInfo: { name: "", ein: "", returnId: "" },
        partners: []
    });

    // Parent mode state
    const [parentModeState, setParentModeState] = useState<ParentModeState>({
        ein: '',
        partnershipInfo: { name: "", ein: "", returnId: "" },
        partners: [],
        childReturnVersion: '',
        childOutputVersion: 'Same',
        childReturnOptions: []
    });

    const token: string = getFromLS("token") ?? "";

    // Helper function to get current mode's state
    const getCurrentState = () => isFetchParent ? parentModeState : childModeState;

    // Helper function to update current mode's state
    const updateCurrentState = (updates: Partial<ChildModeState | ParentModeState>) => {
        if (isFetchParent) {
            setParentModeState(prev => ({ ...prev, ...updates }));
        } else {
            setChildModeState(prev => ({ ...prev, ...updates }));
        }
    };

    // Update the switch handler to preserve state
    const handleModeSwitch = () => {
        setIsProcessing(false);
        setBatchId(null);
        setStatus([]);
        setIsFetchParent(!isFetchParent);
        fetchFlowHistory();
    };

    // Update all the existing state references to use the new state management
    const ein = getCurrentState().ein;
    const setEin = (value: string) => updateCurrentState({ ein: value });

    const availableReturns = isFetchParent ? [] : childModeState.availableReturns;
    const setAvailableReturns = (value: string[]) => updateCurrentState({ availableReturns: value });

    const selectedReturn = isFetchParent ? '' : childModeState.selectedReturn;
    const setSelectedReturn = (value: string) => updateCurrentState({ selectedReturn: value });

    const partnershipInfo = getCurrentState().partnershipInfo;
    const setPartnershipInfo = (value: PartnershipInfo) => updateCurrentState({ partnershipInfo: value });

    const partners = getCurrentState().partners;
    const setPartners = (value: Partner[] | ((prevData: Partner[]) => Partner[])) => {
        if (typeof value === 'function') {
            updateCurrentState({ partners: value(getCurrentState().partners) });
        } else {
            updateCurrentState({ partners: value });
        }
    };

    const childReturnVersion = isFetchParent ? parentModeState.childReturnVersion : '';
    const setChildReturnVersion = (value: string) => updateCurrentState({ childReturnVersion: value });

    const childOutputVersion = isFetchParent ? parentModeState.childOutputVersion : 'Same';
    const setChildOutputVersion = (value: string) => updateCurrentState({ childOutputVersion: value });

    const childReturnOptions = isFetchParent ? parentModeState.childReturnOptions : [];
    const setChildReturnOptions = (value: string[]) => updateCurrentState({ childReturnOptions: value });

    const gridRef = useRef<AgGridReact>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [gridColumnApi, setGridColumnApi] = useState<any>(null);
    const statusBoxRef = useRef<HTMLDivElement>(null);

    const maskSSN = (ssn: string) => {
        if (typeof ssn === 'string' ) {
            return '***-**-' + ssn.slice(-4);
        }
        return ssn;
    };

    // Text mapping system
    const textMapping = {
        einPlaceholder: {
            child: "Enter Partnership EIN",
            parent: "Enter EIN/SSN"
        },
        getReturnsButton: {
            child: "Get Available Returns",
            parent: "Get Parent Returns"
        },
        selectReturnPlaceholder: {
            child: "Select a return",
            parent: "Select parent return"
        },
        showPartnershipButton: {
            child: "Show Partnership Info",
            parent: "Show Partner Info"
        },
        partnershipNameLabel: {
            child: "Partnership Name:",
            parent: "Parent Name:"
        },
        statusUpdates: {
            child: "Status Updates",
            parent: "Status Updates"
        },
        flowK1Button: {
            child: "Flow K1 to Selected Entities",
            parent: "Flow K1 from Selected Entities"
        }
    };

    // Helper function to get text based on component and mode
    const getText = (componentKey: keyof typeof textMapping) => {
        return isFetchParent ? textMapping[componentKey].parent : textMapping[componentKey].child;
    };

    const gridStyle = {
        width: '100%',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
    };

    const columnDefs: ColDef[] = [
        {
            headerName: '',
            field: 'selected',
            checkboxSelection:  true,
            headerCheckboxSelection: true,
            width: 100,
            pinned: 'left' as const,
            cellRenderer: () => '',
            resizable: false,
        },
        { headerName: 'Name', field: 'name', flex: 2 },
        { headerName: 'SSN/EIN', field: 'ein', flex: 1,
            valueFormatter: (params: any) => {
                const ein = params.value;
                if (typeof ein === 'string') {
                    return '**-***' + ein.slice(-4);
                }
                return ein;
            }

         },
        {
            headerName: 'Return ID',
            field: 'selectedReturn',
            flex: 2,
            cellRenderer: (params: any) => (
                <Form.Select
                    value={params.data.selectedReturn}
                    onChange={(e) => handleSelectChange(params.rowIndex, e.target.value)}
                    disabled={isProcessing}
                    className={styles.formSelect}
                >
                    {params.data.return_id_options.map((option: string, idx: number) => (
                        <option key={idx} value={option}>{option}</option>
                    ))}
                </Form.Select>
            )
        },
        ...(isFetchParent ? [
            { headerName: 'Source', field: 'k1Type', flex: 1 },
        ] : [{
            headerName: 'Output Version',
            field: 'selectedOutputVersion',
            flex: 1,
            cellRenderer: (params: any) => (
                <Form.Select
                    value={params.data.selectedOutputVersion}
                    onChange={(e) => handleOutputVersionChange(params.rowIndex, e.target.value)}
                    disabled={isProcessing}
                    className={styles.formSelect}
                >
                    {["Same", "1", "2", "3", "4", "5", "6", "7", "8"].map((option, idx) => (
                        <option key={idx} value={option}>{option}</option>
                    ))}
                </Form.Select>
            )
        }]),
        {
            headerName: 'Status',
            field: 'status',
            flex: 1,
            cellRenderer: (params: any) => (
                <span className={styles[`status${params.data.status.charAt(0).toUpperCase() + params.data.status.slice(1)}`]}>
                    {params.data.status}
                    {params.data.statusMessage && ` - ${params.data.statusMessage}`}
                </span>
            )
        }
    ];

    const defaultColDef: ColDef = {
        sortable: true,
        filter: true,
        resizable: true,
    };

    const onGridReady = (params: GridReadyEvent) => {
        setGridApi(params.api);
        // setGridColumnApi(params.columnApi);
        params.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (params: any) => {
        params.api.sizeColumnsToFit();
    };

    useEffect(() => {
        setToLS("url", ["myflows"])
    }, []);

    // Handle the flow data action
    const handleFlowData = async () => {
        const selectedRows = partners.filter(row => row.selected);
        if (selectedRows.length === 0) {
            addStatusMessage("Please select at least one entity to flow data to.");
            return;
        }

        if (isFetchParent && !childReturnVersion) {
            addStatusMessage("Please select a child return version.");
            return;
        }

        setIsProcessing(true);
        addStatusMessage("Starting data flow process...");

        try {
            if (isFetchParent) {
                // Pull mode - using new APIs
                const selectedParents = selectedRows.map(row => row.selectedReturn);
                const outputVersion = childOutputVersion === "Same" ? "" : childOutputVersion;

                const requestData = {
                    ChildReturnID: childReturnVersion,
                    SelectedParents: selectedParents,
                    OutputVersion: outputVersion
                };

                console.log('Submitting pull batch with data:', requestData);
                console.log('Pull batch request URL:', `${API_URL}/api/submit_pull_batch`);

                const response = await fetch(`${API_URL}/api/submit_pull_batch`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`                        
                    },
                    body: JSON.stringify(requestData)
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                console.log('Pull batch submission response:', result);
                
                if (result.status === 'success') {
                    setBatchId(result.batch_id);
                    addStatusMessage("Pull batch submitted successfully. Processing started...");
                    
                    setPartners(prevData => 
                        prevData.map(row => ({
                            ...row,
                            status: row.selected ? 'processing' : row.status
                        }))
                    );

                    // Reload flow history after a short delay
                    setTimeout(() => {
                        if (showHistory) {
                            fetchFlowHistory();
                        }
                    }, 2000);
                } else {
                    throw new Error(result.message || 'Failed to submit pull batch');
                }
            } else {
                // Push mode - using existing APIs
                const selectedPartners = selectedRows.map(row => row.selectedReturn);
                const outputVersions = selectedRows.map(row => row.selectedOutputVersion);

                console.log('Submitting push batch with data:', {
                    PartnershipReturnID: partnershipInfo.returnId,
                    SelectedPartners: selectedPartners,
                    OutputVersions: outputVersions
                });

                const response = await fetch(`${API_URL}/api/submit_batch`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        PartnershipReturnID: partnershipInfo.returnId,
                        SelectedPartners: selectedPartners,
                        OutputVersions: outputVersions
                    })
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                console.log('Push batch submission response:', result);
                
                if (result.status === 'success') {
                    setBatchId(result.batch_id);
                    addStatusMessage("Push batch submitted successfully. Processing started...");
                    
                    setPartners(prevData => 
                        prevData.map(row => ({
                            ...row,
                            status: row.selected ? 'processing' : row.status
                        }))
                    );

                    // Reload flow history after a short delay
                    setTimeout(() => {
                        if (showHistory) {
                            fetchFlowHistory();
                        }
                    }, 2000);
                } else {
                    throw new Error(result.message || 'Failed to submit push batch');
                }
            }
        } catch (error) {
            console.error('Error submitting batch:', error);
            addStatusMessage(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setIsProcessing(false);
        }
    };

    // Poll for batch status updates
    useEffect(() => {
        if (!batchId) return;

        const interval = setInterval(async () => {
            try {
                const endpoint = isFetchParent ? 'pull_batch_status' : 'batch_status';
                const response = await fetch(`${API_URL}/api/${endpoint}?batch_id=${batchId}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                const data: BatchStatusResponse = await response.json();
                
                if (data.status === 'success') {
                    setPartners(prevData => 
                        prevData.map(row => {
                            const partnerStatus = isFetchParent 
                                ? data.parents?.[row.selectedReturn]
                                : data.partners?.[row.selectedReturn];
                            if (!partnerStatus) return row;
                            
                            let displayStatus = partnerStatus.status.toLowerCase();
                            if (displayStatus === 'pending') displayStatus = 'pending';
                            if (displayStatus === 'processing') displayStatus = 'processing';
                            if (displayStatus === 'complete') displayStatus = 'completed';
                            if (displayStatus === 'error') displayStatus = 'error';
                            
                            return {
                                ...row,
                                status: displayStatus,
                                statusMessage: partnerStatus.message
                            };
                        })
                    );

                    const statuses = isFetchParent ? data.parents : data.partners;
                    /*const allComplete = statuses ? Object.values(statuses).every(
                        (p: PartnerStatus) => p.status === 'Complete' || p.status === 'Error'
                    ) : false;
                    
                    if (allComplete) {
                        setBatchId(null);
                        setIsProcessing(false);
                        addStatusMessage("Processing completed.");
                        setTimeout(() => {
                            if (showHistory) {
                                fetchFlowHistory();
                            }
                        }, 2000);
                    }*/
                    if (statuses) {
                        if(statuses.BATCH.status === 'Complete') {
                            setBatchId(null);
                            setIsProcessing(false);
                            addStatusMessage("Processing completed.");
                            if (showHistory) {
                                fetchFlowHistory();
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error checking batch status:', error);
                addStatusMessage(`Error checking status: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [batchId, isFetchParent]);

    // Add this useEffect for auto-scrolling status updates
    useEffect(() => {
        if (statusBoxRef.current) {
            const scrollToBottom = () => {
                if (statusBoxRef.current) {
                    statusBoxRef.current.scrollTop = statusBoxRef.current.scrollHeight;
                }
            };
            
            // Scroll immediately
            scrollToBottom();
            
            // Scroll after a short delay to ensure content is rendered
            const timeoutId = setTimeout(scrollToBottom, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [status]);

    // Helper function to append a status message
    const addStatusMessage = (message: string) => {
        setStatus(prevMessages => [...prevMessages, message]);
    };

    // Fetch available returns for the EIN
    const handleGetReturns = async () => {
        if (!ein) {
            addStatusMessage("Please enter a Partnership EIN");
            return;
        }

        setIsLoadingReturns(true);
        addStatusMessage("Loading available returns...");
        setAvailableReturns([]);
        setSelectedReturn('');
        setPartners([]);
        
        try {
            const url = `${API_URL}/api/get_available_returns?EIN=${encodeURIComponent(ein)}`;
            console.log('Making request to:', url);
            
            const returnsResponse = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                cache: 'no-store',
                mode: 'cors'
            });
            
            console.log('Response status:', returnsResponse.status);
            console.log('Response headers:', Object.fromEntries(returnsResponse.headers.entries()));
            
            if (!returnsResponse.ok) {
                const errorText = await returnsResponse.text();
                console.error('Error response:', errorText);
                throw new Error(`HTTP error! status: ${returnsResponse.status}, message: ${errorText}`);
            }
            
            const returnsData = await returnsResponse.json();
            console.log('Raw response data:', returnsData);
            console.log('Response type:', typeof returnsData);
            console.log('Response keys:', Object.keys(returnsData));
            
            if (!returnsData.return_ids || returnsData.return_ids.length === 0) {
                console.log('No returns found in response');
                throw new Error('No returns found for this EIN');
            }

            console.log('Setting available returns:', returnsData.return_ids);
            setAvailableReturns(returnsData.return_ids);
            addStatusMessage("Available returns loaded successfully. Please select a return.");
        } catch (error) {
            console.error('Detailed error:', error);
            console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace');
            let errorMessage = error instanceof Error ? error.message : 'Unknown error';
            if (errorMessage.includes('<!doctype')) {
                errorMessage = 'Server error: Failed to process the request. Please try again or contact support if the issue persists.';
            }
            addStatusMessage(`Error: ${errorMessage}`);
            setAvailableReturns([]);
        } finally {
            setIsLoadingReturns(false);
        }
    };

    // Fetch table data for the selected return
    const handleGetTableData = async () => {
        if (isFetchParent && !ein) {
            addStatusMessage("Please enter a Child SSN/EIN");
            return;
        }

        if (!isFetchParent && !selectedReturn) {
            addStatusMessage("Please select a return from the dropdown");
            return;
        }

        addStatusMessage("Loading partnership data...");
        setPartners([]);
        
        try {
            console.log('Fetching table data for return ID:', selectedReturn);
            
            // Add retry logic for the table data request
            let retries = 3;
            let tableResponse: Response | undefined;
            let responseText: string | null = null;
            
            while (retries > 0) {
                try {
                    const url = isFetchParent 
                        ? `${API_URL}/api/get_pull_table_data?child_ssn=${encodeURIComponent(ein)}`
                        : `${API_URL}/api/get_table_data?return_id=${encodeURIComponent(selectedReturn)}`;

                    tableResponse = await fetch(url, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        credentials: 'include',
                        cache: 'no-store',
                        mode: 'cors'
                    });
                    
                    if (tableResponse.ok) break;
                    
                    // Store the response text so we don't try to read it again
                    responseText = await tableResponse.text();
                    console.error(`Attempt ${4-retries} failed:`, responseText);
                    retries--;
                    
                    if (retries > 0) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                } catch (error) {
                    console.error(`Attempt ${4-retries} failed:`, error);
                    retries--;
                    if (retries === 0) throw error;
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }

            if (!tableResponse) {
                throw new Error('Failed to get response after retries');
            }

            if (!tableResponse.ok) {
                // Use the stored response text if available, otherwise read it again
                const errorText = responseText || await tableResponse.text();
                throw new Error(`Failed to fetch table data after retries: ${errorText}`);
            }

            const data = await tableResponse.json();
            console.log('Table data response:', data);

            // Handle different response formats based on mode
            if (isFetchParent) {
                // Handle parent mode response
                // alert("Pull Mode");
                if (data.status === 'failure') {
                    throw new Error(data.message || 'Failed to load child data');
                }

                if (!data.ChildClientID || !data.ChildName || !data.ChildSSN) {
                    throw new Error('Invalid response: Missing child information');
                }

                setPartnershipInfo({
                    name: data.ChildName,
                    ein: maskSSN(data.ChildSSN),
                    returnId: data.ChildClientID
                });

                // Set child return options from the child's return options
                if (data.ChildReturnOptions && Array.isArray(data.ChildReturnOptions) && data.ChildReturnOptions.length > 0) {
                    console.log('Child return options:', data.ChildReturnOptions);
                    
                    setChildReturnOptions(data.ChildReturnOptions);
                    setChildReturnVersion(data.ChildReturnOptions[0]);
                } else {
                    throw new Error('Invalid response: ChildReturnOptions is missing or empty');
                }

                if (!data.TableData || !Array.isArray(data.TableData)) {
                    throw new Error('Invalid response: TableData is missing or not an array');
                }

                const modifiedData = data.TableData.map((row: any) => ({
                    ...row,
                    selected: false,
                    selectedReturn: row.return_id_options[0],
                    k1Type: "Internal",
                    status: 'Idle'
                }));
                const externalK1Data = data.ExternalParentData.map((row: any) => ({
                    ein: "NA",
                    id: row.id,
                    name: row.name,
                    return_id_options: [],
                    selected: false,
                    selectedReturn: "",
                    k1Type: "External",
                    status: "Idle"

                }));
                
                const combinedPartners = [...modifiedData, ...externalK1Data];

                setPartners(combinedPartners);
                
                
                // setPartners(modifiedData);
            } else {
                // Handle child mode response (existing logic)
                if (data.status === 'failure') {
                    throw new Error(data.message || 'Failed to load partnership data');
                }

                if (!data.PartnershipName || !data.PartnershipReturnID) {
                    throw new Error('Invalid response: Missing partnership information');
                }

                setPartnershipInfo({
                    name: data.PartnershipName,
                    ein: ein,
                    returnId: data.PartnershipReturnID
                });

                if (!data.TableData || !Array.isArray(data.TableData)) {
                    throw new Error('Invalid response: TableData is missing or not an array');
                }

                const modifiedData = data.TableData.map((row: any) => ({
                    ...row,
                    selected: false,
                    selectedReturn: row.return_id_options[0],
                    selectedOutputVersion: "Same",
                    status: 'Idle'
                }));

                
                
                setPartners(modifiedData);
            }

            addStatusMessage("Partnership data loaded successfully.");
        } catch (error) {
            console.error('Detailed error:', error);
            let errorMessage = error instanceof Error ? error.message : 'Unknown error';
            if (errorMessage.includes('<!doctype')) {
                errorMessage = 'Server error: Failed to process the request. Please try again or contact support if the issue persists.';
            }
            addStatusMessage(`Error: ${errorMessage}`);
            setPartners([]);
            setPartnershipInfo({
                name: "",
                ein: ein,
                returnId: ""
            });
        }
    };

    // Update the selected dropdown value for a given row
    const handleSelectChange = (index: number, value: string) => {
        setPartners((prevData: Partner[]) => {
            const newData = [...prevData];
            newData[index].selectedReturn = value;
            return newData;
        });
    };

    // Update the output version for a given row
    const handleOutputVersionChange = (index: number, value: string) => {
        setPartners((prevData: Partner[]) => {
            const newData = [...prevData];
            newData[index].selectedOutputVersion = value;
            return newData;
        });
    };

    // Fetch flow history
    const fetchFlowHistory = async () => {
        setIsLoadingHistory(true);
        try {
            const params = new URLSearchParams();
            if (ein) {
                if (isFetchParent) {
                     params.append('partner_ssn', ein)
                } else {
                     params.append('partnership_ein', ein);
                }
            }

            params.append('max_results', '10'); // Default to 10 results

            console.log('Fetching flow history with params:', params.toString());

            const response = await fetch(`${API_URL}/api/flow_history?${params.toString()}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                cache: 'no-store',
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: FlowHistoryResponse = await response.json();
            console.log('Flow history response:', data);
            
            if (data.status === 'success') {
                setHistory(data.data);
            } else {
                throw new Error('Failed to fetch history');
            }
        } catch (error) {
            console.error('Error fetching history:', error);
            addStatusMessage(`Error fetching history: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Format timestamp for display
    const formatTimestamp = (timestamp: string) => {
        //console.log('Original timestamp:', timestamp);
        // Parse the ISO timestamp and convert to local time
        const localTime = dayjs(timestamp).local();
        //console.log('Local time:', localTime.format());
        //console.log('Current timezone:', dayjs.tz.guess());
        //console.log('Current local time:', dayjs().format());
        
        return localTime.format('MM/DD/YYYY hh:mm:ss A');
    };

    // Add a function to clear the current mode's state
    const handleClearPage = () => {
        if (isFetchParent) {
            // Clear parent mode state
            setParentModeState({
                ein: '',
                partnershipInfo: { name: "", ein: "", returnId: "" },
                partners: [],
                childReturnVersion: '',
                childOutputVersion: 'Same',
                childReturnOptions: []
            });
        } else {
            // Clear child mode state
            setChildModeState({
                ein: '',
                availableReturns: [],
                selectedReturn: '',
                partnershipInfo: { name: "", ein: "", returnId: "" },
                partners: []
            });
        }
        
        // Clear common state
        setStatus([]);
        setIsProcessing(false);
        setBatchId(null);
    };

    return (
        <div className={styles.container}>
            <InfoModal
                            show={showInfoModal}
                            // submit={(returnId) => importSelectedKonesToTax(selectedRows, returnId)}
                            onHide={() => setShowInfoModal(false)}
                            type = {isFetchParent ? "pull" : "push"}
                        />
            <div className={styles.headerContainer}>
                <h2 className={styles.heading}>Internal K1 Data Flow Manager</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <FaQuestionCircle onClick={() => setShowInfoModal(true)} />
                    <Switch 
                        isToggled={isFetchParent}
                        onClick={handleModeSwitch}
                        label={isFetchParent ? "Pull Mode" : "Push Mode"}
                    />
                    <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={handleClearPage}
                        title="Clear current page"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <FaTrashAlt /> Clear
                    </Button>
                </div>
            </div>
            
            <div className={styles.topSection}>
                <div className={styles.leftSection}>
                    <div className="mb-4">
                        <Row>
                            <Col md={4}>
                                <Form.Control 
                                    type="text" 
                                    placeholder={getText('einPlaceholder')}
                                    value={ein}
                                    onChange={(e) => setEin(e.target.value)}
                                    disabled={isProcessing || isLoadingReturns}
                                    className={styles.formControl}
                                />
                            </Col>
                            {!isFetchParent && (
                                <Col md={4}>
                                    <Button 
                                        variant="secondary" 
                                        onClick={handleGetReturns}
                                        disabled={isProcessing || isLoadingReturns}
                                        className={styles.secondaryButton}
                                    >
                                        {isLoadingReturns ? 'Loading...' : getText('getReturnsButton')}
                                    </Button>
                                </Col>
                            )}
                        </Row>

                        {!isFetchParent && (
                            <Row className="mt-3">
                                <Col md={4}>
                                    <Form.Select
                                        value={selectedReturn}
                                        onChange={(e) => setSelectedReturn(e.target.value)}
                                        disabled={isProcessing}
                                        className={styles.formSelect}
                                    >
                                        <option value="">{getText('selectReturnPlaceholder')}</option>
                                        {availableReturns.map((returnId, index) => (
                                            <option key={index} value={returnId}>
                                                {returnId}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={4}>
                                    <Button 
                                        variant="primary" 
                                        onClick={handleGetTableData}
                                        disabled={isProcessing || !selectedReturn}
                                        className={styles.customButton}
                                    >
                                        {getText('showPartnershipButton')}
                                    </Button>
                                </Col>
                            </Row>
                        )}

                        {isFetchParent && (
                            <Row className="mt-3">
                                <Col md={4}>
                                    <Button 
                                        variant="primary" 
                                        onClick={handleGetTableData}
                                        disabled={isProcessing || !ein}
                                        className={styles.customButton}
                                    >
                                        {getText('showPartnershipButton')}
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </div>

                    <div className={styles.infoBox}>
                        <p data-label={getText('partnershipNameLabel')}>{partnershipInfo.name || '-'}</p>
                        <p data-label="EIN:">{partnershipInfo.ein || '-'}</p>
                        <p data-label="Client ID:">{partnershipInfo.returnId || '-'}</p>
                    </div>
                </div>

                <div className={styles.rightSection}>
                    <h4>{getText('statusUpdates')}</h4>
                    <div className={styles.statusBox} ref={statusBoxRef}>
                        <pre>{status.join('\n')}</pre>
                    </div>
                </div>
            </div>

            <div className={styles.buttonContainer}>
                <Button
                    variant="secondary"
                    onClick={handleFlowData}
                    disabled={isProcessing || !partners.some(row => row.selected)}
                    className={styles.secondaryButton}
                >
                    {getText('flowK1Button')}
                </Button>
            </div>

            <div className={styles.integrationWorkflow}>
                <div className="ag-theme-balham" style={gridStyle}>
                    <AgGridReact
                        ref={gridRef}
                        rowData={partners}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        onGridReady={onGridReady}
                        alwaysShowHorizontalScroll={false}
                        alwaysShowVerticalScroll={false}
                        rowHeight={50}
                        headerHeight={38}
                        suppressContextMenu={true}
                        onFirstDataRendered={onFirstDataRendered}
                        domLayout="autoHeight"
                        getRowHeight={() => 50}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        onSelectionChanged={(event) => {
                            const selectedNodes = event.api.getSelectedNodes();
                            const newData = [...partners];
                            newData.forEach(row => {
                                row.selected = selectedNodes.some(node => node.data.id === row.id);
                            });
                            setPartners(newData);
                        }}
                        isRowSelectable={(params) => {
                            return !isProcessing && params.data?.k1Type !== 'External';
                          }}
                    />
                </div>
            </div>

            {isFetchParent && partners.length > 0 && (
                <div className={styles.childVersionSection}>
                    <Row className="mt-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Child Return Version</Form.Label>
                                <Form.Select
                                    value={childReturnVersion}
                                    onChange={(e) => setChildReturnVersion(e.target.value)}
                                    disabled={isProcessing}
                                    className={styles.formSelect}
                                >
                                    {childReturnOptions.map((option, index) => (
                                        <option key={index} value={option}>{option}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Child Output Version</Form.Label>
                                <Form.Select
                                    value={childOutputVersion}
                                    onChange={(e) => setChildOutputVersion(e.target.value)}
                                    disabled={isProcessing}
                                    className={styles.formSelect}
                                >
                                    {["Same", "1", "2", "3", "4", "5", "6", "7", "8"].map((option, idx) => (
                                        <option key={idx} value={option}>{option}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            )}

            <Accordion className={styles.historyAccordion}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header onClick={() => {
                        setShowHistory(!showHistory);
                        if (!showHistory) {
                            fetchFlowHistory();
                        }
                    }}>
                        Flow History
                    </Accordion.Header>
                    <Accordion.Body>
                        {isLoadingHistory ? (
                            <div className={styles.loadingMessage}>Loading history...</div>
                        ) : history.length === 0 ? (
                            <div className={styles.noHistoryMessage}>No flow history available</div>
                        ) : (
                            <Table bordered className={styles.historyTable}>
                                <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>Partnership</th>
                                        <th>Partner</th>
                                        <th>Version</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((item, index) => (
                                        <tr key={index}>
                                            <td>{formatTimestamp(item.timestamp)}</td>
                                            <td>
                                                <div>{item.partnership_name}</div>
                                                <small className="text-muted">{item.partnership_return_id}</small>
                                            </td>
                                            <td>
                                                <div>{item.partner_name}</div>
                                                <small className="text-muted">{item.partner_return_id}</small>
                                            </td>
                                            <td>{item.dest_version}</td>
                                            <td>
                                                <span className={styles[`status${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`]}>
                                                    {item.status}
                                                    {item.message && ` - ${item.message}`}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
             
        </div>
    );
}

export default InternalK1;
